import React, {useEffect, useState} from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import OtpInput from 'react-otp-input';
import {useHistory} from 'react-router-dom';

import {LanguageSelector} from 'components/common/LanguageSelector';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {TitleLogoComponent} from 'components/ui-kit/TitleLogo';
import {LABELS} from 'core/constants/labels';
import {ROUTE} from 'core/routes';
import {otpState$, resetAuthData, sendOtpFx, tempEmail$, verifyOtpFx} from 'models/auth';

import {colors} from 'theme';

import {
    ControlOTPContainer,
    CustomDatamatrixIcon,
    FormDescription,
    LanguageWrapper,
    MainContainer,
    OptWrapper,
    PasswordContent,
    PasswordTitle,
    ServiceTitle,
    Timer,
} from './styled';

export function OTPPage() {
    const {t} = useTranslation();
    const history = useHistory();
    const [otp, setOtp] = useState('');
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [timeLeft, setTimeLeft] = useState(120);
    const [autoConfirm, setAutoConfirm] = useState(false);

    const otpState = useStore(otpState$);
    const email = useStore(tempEmail$);

    useEffect(() => {
        autoConfirm && otp.length === 6 && onConfirm();
    }, [otp]);

    useEffect(() => {
        if (!email && !otpState.pending) history.push(ROUTE.LOGIN);
    }, [email]);

    useEffect(() => {
        if (timeLeft === 0) return;
        const timer = setInterval(() => setTimeLeft((t) => t - 1), 1000);
        return () => clearInterval(timer);
    }, [timeLeft]);

    const onConfirm = () => {
        if (otp.length === 6 && sessionId) {
            verifyOtpFx({code: otp, email, sessionId});
            setAutoConfirm(false);
        }
    };

    const onResend = async () => {
        setAutoConfirm(true);
        setOtp('');
        if (!email) return;

        try {
            const id = await sendOtpFx({email, userName: email});
            setSessionId(id);
            setTimeLeft(120);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        onResend();
    }, []);

    return (
        <>
            <div>
                <ServiceTitle size={4}>
                    <TitleLogoComponent />
                    {LABELS.name}
                </ServiceTitle>
                <LanguageWrapper>
                    <LanguageSelector />
                </LanguageWrapper>
            </div>
            <MainContainer>
                <PasswordContent>
                    <PasswordTitle size={3}>{t('pages.otpVerify.title')}</PasswordTitle>
                    <FormDescription>
                        {t('pages.otpVerify.description')}{' '}
                        <span style={{fontWeight: 700}}>{email}</span>
                    </FormDescription>

                    <OptWrapper>
                        <OtpInput
                            value={otp}
                            onChange={setOtp}
                            numInputs={6}
                            renderInput={(props) => <input {...props} />}
                            renderSeparator={
                                <span
                                    style={{
                                        color: otpState.error ? colors.danger : colors.textMuted,
                                    }}
                                >
                                    —
                                </span>
                            }
                            inputStyle={{
                                width: '48px',
                                height: '42px',
                                margin: '0 8px',
                                fontSize: '24px',
                                borderRadius: '6px',
                                outline: 'none',
                                border: `1px solid ${
                                    otpState.error ? colors.danger : colors.textMuted
                                }`,
                            }}
                            inputType="tel"
                            shouldAutoFocus
                        />
                    </OptWrapper>

                    <Timer>
                        {timeLeft > 0 ? (
                            <span>
                                {t('pages.otpVerify.timer', {
                                    min: Math.floor(timeLeft / 60),
                                    sec: (timeLeft % 60).toString().padStart(2, '0'),
                                })}
                            </span>
                        ) : (
                            <span style={{color: colors.danger}}>
                                {t('pages.otpVerify.expired')}
                            </span>
                        )}
                    </Timer>

                    <ControlOTPContainer>
                        <Button
                            variant={ButtonVariant.Outline}
                            onClick={onResend}
                            isLoading={otpState.sending}
                            disabled={timeLeft > 0}
                        >
                            {t('pages.otpVerify.resendButton')}
                        </Button>

                        <Button
                            onClick={onConfirm}
                            isLoading={otpState.pending}
                            disabled={otp.length < 6 || timeLeft === 0}
                        >
                            {t('pages.otpVerify.confirmButton')}
                        </Button>

                        <Button
                            style={{gridColumn: '1 / -1'}}
                            variant={ButtonVariant.Text}
                            onClick={() => resetAuthData()}
                        >
                            {t('pages.otpVerify.returnButton')}
                        </Button>
                    </ControlOTPContainer>
                </PasswordContent>
            </MainContainer>
        </>
    );
}
